// usePointerMode などからの「ユーザー操作」を CanvasOp へ変換し、
// 楽観更新 (applyOptimistic) → realtime クライアント送信を一括で行う dispatcher。

import { useCallback, useRef } from "react";
import { useShowToast } from "@meiden-connect/ui";
import {
  CANVAS_OBJECT_HARD_LIMIT,
  CANVAS_OBJECT_SOFT_LIMIT,
  clampCanvasObjectToCanvas,
  clampPointToCanvas,
  computePenStrokeFrame,
  isCanvasObjectInsideCanvas,
  type CanvasOp,
  type CanvasObject,
  type PenStrokeCanvasObject,
  type StickyCanvasObject,
  type TextCanvasObject,
  type ShapeCanvasObject,
  type ImageCanvasObject,
  type VideoCanvasObject,
  type LinkCardCanvasObject,
  type ConnectorEndpoint,
  type ConnectorFrame,
  connectorStoredFrame,
  collectConnectorCascadeDeleteIds,
} from "@refinement-canvas/domain";
import { customAlphabet } from "nanoid";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useRealtimeClient } from "../../realtime/realtime-context";

const generateId = customAlphabet("23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ", 16);

interface DispatcherContext {
  readonly actorUserId: string;
  /**
   * 操作者の表示名（日本語名）。op.actorDisplayName へ転写し、サーバが sticky の
   * updatedByName 等へ反映するための情報源。未指定なら省略。
   */
  readonly actorDisplayName?: string | undefined;
}

// CanvasObject は discriminated union のため、Omit を distributive に展開して
// 個別 type の draft 入力（text / shapeKind / src 等）を保持できるようにする。
type DistributiveDraft<T> = T extends CanvasObject
  ? Omit<T, "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"> & {
      readonly id?: string | undefined;
    }
  : never;

export type CanvasObjectDraft = DistributiveDraft<CanvasObject>;

export interface CanvasOpDispatcher {
  readonly createObject: (object: CanvasObjectDraft) => CanvasObject | null;
  readonly createObjects: (objects: readonly CanvasObjectDraft[]) => readonly CanvasObject[];
  /**
   * 図形と図形を結ぶコネクタ（接続矢印）を作成する。
   * 端点（from / to）の現在位置からルートを算出し、その外接矩形を保存用 x/y/w/h に用いる。
   * 端点オブジェクトが存在しない場合や自己ループの場合は作成せず null を返す。
   */
  readonly createConnector: (
    from: ConnectorEndpoint,
    to: ConnectorEndpoint,
    stroke?: string | undefined
  ) => CanvasObject | null;
  /**
   * 画像・動画の添付を「先に仮表示、あとから本体同期」で配置する。
   *
   * - `object.src` には軽量なプレースホルダ（画像なら超圧縮 LQIP の data URL、
   *   動画なら 1x1 透明 GIF など）を入れて全参加者へ create を即配信する。
   *   blob: URL は他参加者が解決できないため、ここには絶対に入れない。
   * - 作成者本人だけは即フル画質を見られるよう、送信しないローカル専用の
   *   attachment.update（src=localFullSrc=blob URL）を重ねて適用する。
   *
   * 戻り値の overrideOpId は、本体アップロード完了後に
   * {@link finalizeAttachmentPlaceholder} / {@link discardAttachmentPlaceholder} へ渡す。
   */
  readonly createAttachmentPlaceholder: (
    object: CanvasObjectDraft,
    localFullSrc: string
  ) => { readonly object: CanvasObject; readonly overrideOpId: string } | null;
  /**
   * アップロード完了時に呼ぶ。公開 URL を全参加者へ attachment.update で配信し、
   * 本人用ローカル上書き（blob プレビュー）を破棄して全員を公開 URL 表示へ収束させる。
   */
  readonly finalizeAttachmentPlaceholder: (
    objectId: string,
    overrideOpId: string,
    publicSrc: string
  ) => void;
  /**
   * アップロード失敗時に呼ぶ。本人用ローカル上書きを破棄し、仮表示オブジェクトを
   * 全参加者から削除する（壊れた状態を残さない）。
   */
  readonly discardAttachmentPlaceholder: (objectId: string, overrideOpId: string) => void;
  readonly transformObject: (objectId: string, transform: ObjectTransformPatch) => void;
  /**
   * 複数オブジェクトの transform を **1 回の履歴エントリ** にまとめて適用する。
   * グループドラッグなど「ユーザーから見れば 1 操作」を 1 回の Undo で戻せるようにする。
   */
  readonly transformObjects: (
    updates: readonly { readonly objectId: string; readonly transform: ObjectTransformPatch }[]
  ) => void;
  readonly updateText: (objectId: string, text: string) => void;
  /** 色・フォントサイズ・文字寄せ等の見た目だけを変更する。位置・サイズは transformObject 側を使う。 */
  readonly updateObjectStyle: (
    objectId: string,
    style: Partial<{
      readonly color: string;
      readonly fontSize: number;
      readonly fill: string;
      readonly stroke: string;
      readonly textAlign: "left" | "center" | "right";
    }>
  ) => void;
  readonly updateAttachmentSrc: (objectId: string, src: string) => void;
  readonly updateCanvasMeta: (input: { title?: string | undefined; thumbnailKey?: string | undefined }) => void;
  readonly deleteObject: (objectId: string) => void;
  readonly deleteObjects: (objectIds: readonly string[]) => void;
  readonly undo: () => void;
  readonly redo: () => void;
  readonly commitPenStroke: (
    points: ReadonlyArray<{ x: number; y: number; pressure: number }>,
    color: string,
    thickness: number,
    boundingBox: { x: number; y: number; w: number; h: number },
    /** 描画中 transient と同一の strokeId。確定オブジェクト ID に採用してゴースト破棄を正確化する。 */
    objectId?: string
  ) => PenStrokeCanvasObject | null;
  /**
   * 操作中（未確定）の transform を他参加者へ一時 op として中継する。
   * ゴースト表示用であり永続化しない。ドラッグ移動 / リサイズ / 回転の途中経過に使う。
   */
  readonly previewTransforms: (
    updates: readonly { readonly objectId: string; readonly transform: ObjectTransformPatch }[]
  ) => void;
  /** 描画中ペンストロークの増分点を他参加者へ一時 op として中継する（ゴースト表示用）。 */
  readonly previewPenAppend: (
    strokeId: string,
    points: ReadonlyArray<{ x: number; y: number; pressure: number }>
  ) => void;
}

export type ObjectTransformPatch = Partial<{
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  zOrder: number;
  locked: boolean;
}>;

interface HistoryEntry {
  readonly undo: readonly CanvasOp[];
  readonly redo: readonly CanvasOp[];
}

// op に共通して付与するメタ情報（makeMeta の戻り値）。全 op が BaseOp として持つ。
type OpMeta = Pick<
  CanvasOp,
  "clientOpId" | "clientCreatedAt" | "actorUserId" | "actorDisplayName"
>;

const undoStack: HistoryEntry[] = [];
const redoStack: HistoryEntry[] = [];
const HISTORY_LIMIT = 100;

export function clearCanvasHistory(): void {
  undoStack.length = 0;
  redoStack.length = 0;
}

export function useCanvasOpDispatcher(ctx: DispatcherContext): CanvasOpDispatcher {
  const applyOptimistic = useCanvasDocStore((s) => s.applyOptimistic);
  const removeInFlightOp = useCanvasDocStore((s) => s.removeInFlightOp);
  const realtime = useRealtimeClient();
  const showToast = useShowToast();
  const lastSoftLimitToastAtRef = useRef(0);

  const sendOp = useCallback(
    (op: CanvasOp): void => {
      applyOptimistic(op);
      realtime.sendOp(op);
    },
    [applyOptimistic, realtime]
  );

  const dispatch = useCallback(
    (op: CanvasOp, history?: HistoryEntry | undefined): void => {
      if (history) {
        undoStack.push(history);
        if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
        redoStack.length = 0;
      }
      sendOp(op);
    },
    [sendOp]
  );

  const dispatchMany = useCallback(
    (ops: readonly CanvasOp[], history?: HistoryEntry | undefined): void => {
      if (ops.length === 0) return;
      if (history) {
        undoStack.push(history);
        if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
        redoStack.length = 0;
      }
      for (const op of ops) {
        sendOp(op);
      }
    },
    [sendOp]
  );

  const makeMeta = useCallback(
    () => ({
      clientOpId: generateId(),
      clientCreatedAt: new Date().toISOString(),
      actorUserId: ctx.actorUserId,
      // displayName が無い場合はキーごと送らず、旧来 op 形式と差分が出ないようにする。
      ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
    }),
    [ctx.actorUserId, ctx.actorDisplayName]
  );

  const canCreateObjects = useCallback(
    (addCount: number): boolean => {
      const currentCount = Object.keys(useCanvasDocStore.getState().objects).length;
      const nextCount = currentCount + addCount;
      if (nextCount > CANVAS_OBJECT_HARD_LIMIT) {
        showToast(
          "error",
          `このキャンバスはオブジェクト数の上限（${CANVAS_OBJECT_HARD_LIMIT.toLocaleString()}件）に達しています。新しいキャンバスへ分割して作業を続けてください。`
        );
        return false;
      }

      if (nextCount >= CANVAS_OBJECT_SOFT_LIMIT) {
        const now = Date.now();
        if (now - lastSoftLimitToastAtRef.current > 30_000) {
          lastSoftLimitToastAtRef.current = now;
          showToast(
            "warning",
            `このキャンバスのオブジェクト数が多くなっています（${nextCount.toLocaleString()}件）。動作が重い場合は、新しいキャンバスへの分割をご検討ください。`
          );
        }
      }
      return true;
    },
    [showToast]
  );

  const notifyOutOfBounds = useCallback((): void => {
    showToast("warning", "キャンバス範囲外には新しいオブジェクトを作成できません。");
  }, [showToast]);

  // create 用の object と op を組み立てる共通処理。上限・範囲チェックを行い、
  // 通過しなければ null を返す（即時 create と「仮表示プレースホルダ」create で共有する）。
  const buildCreateOp = useCallback(
    (input: CanvasObjectDraft): { object: CanvasObject; op: CanvasOp } | null => {
      if (!canCreateObjects(1)) return null;
      const id = input.id ?? generateId();
      const now = new Date().toISOString();
      const objects = useCanvasDocStore.getState().objects;
      const nextZ = computeNextZOrder(objects);
      const baseObject = clampCanvasObjectToCanvas({
        ...input,
        id,
        version: 1,
        zOrder: nextZ,
        createdAt: now,
        createdBy: ctx.actorUserId,
        updatedAt: now,
        updatedBy: ctx.actorUserId,
      } as CanvasObject);
      // 付箋の初期作成時にも、最終更新者名スナップショットを埋め込む（後続編集と同じ挙動にする）。
      const object: CanvasObject =
        baseObject.type === "sticky" && ctx.actorDisplayName
          ? { ...baseObject, updatedByName: ctx.actorDisplayName }
          : baseObject;
      if (!isCanvasObjectInsideCanvas(object)) {
        notifyOutOfBounds();
        return null;
      }
      const op: CanvasOp = {
        type: "object.create",
        clientOpId: generateId(),
        clientCreatedAt: now,
        actorUserId: ctx.actorUserId,
        ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
        object,
      };
      return { object, op };
    },
    [canCreateObjects, ctx.actorUserId, ctx.actorDisplayName, notifyOutOfBounds]
  );

  const createObject = useCallback<CanvasOpDispatcher["createObject"]>(
    (input) => {
      const built = buildCreateOp(input);
      if (!built) return null;
      dispatch(built.op, {
        undo: [makeDeleteOp(built.object.id, ctx.actorUserId, ctx.actorDisplayName)],
        redo: [built.op],
      });
      return built.object;
    },
    [buildCreateOp, ctx.actorUserId, dispatch]
  );

  const createConnector = useCallback<CanvasOpDispatcher["createConnector"]>(
    (from, to, stroke) => {
      // 自己ループは作らない。
      if (from.objectId === to.objectId) return null;
      const objects = useCanvasDocStore.getState().objects;
      const fromObj = objects[from.objectId];
      const toObj = objects[to.objectId];
      if (!fromObj || !toObj) return null;
      const fromFrame: ConnectorFrame = { x: fromObj.x, y: fromObj.y, w: fromObj.w, h: fromObj.h };
      const toFrame: ConnectorFrame = { x: toObj.x, y: toObj.y, w: toObj.w, h: toObj.h };
      const bbox = connectorStoredFrame(fromFrame, from.side, toFrame, to.side);
      return createObject({
        type: "connector",
        from,
        to,
        x: bbox.x,
        y: bbox.y,
        w: bbox.w,
        h: bbox.h,
        ...(stroke ? { stroke } : {}),
      });
    },
    [createObject]
  );

  const createAttachmentPlaceholder = useCallback<
    CanvasOpDispatcher["createAttachmentPlaceholder"]
  >(
    (input, localFullSrc) => {
      const built = buildCreateOp(input);
      if (!built) return null;
      // 1) プレースホルダ src を載せた create を全参加者へ配信する（peer 側の仮表示）。
      dispatch(built.op, {
        undo: [makeDeleteOp(built.object.id, ctx.actorUserId, ctx.actorDisplayName)],
        redo: [built.op],
      });
      // 2) 作成者本人だけがフル画質を即見られるよう、送信しないローカル専用の
      //    attachment.update を重ねる（inFlightOps に残るため再構築でも維持される）。
      const overrideOp: CanvasOp = {
        type: "object.attachment.update",
        ...makeMeta(),
        objectId: built.object.id,
        src: localFullSrc,
      };
      applyOptimistic(overrideOp);
      return { object: built.object, overrideOpId: overrideOp.clientOpId };
    },
    [buildCreateOp, ctx.actorUserId, dispatch, makeMeta, applyOptimistic]
  );

  const finalizeAttachmentPlaceholder = useCallback<
    CanvasOpDispatcher["finalizeAttachmentPlaceholder"]
  >(
    (objectId, overrideOpId, publicSrc) => {
      // 表示中かどうかに関わらず、まず履歴（undo/redo 両側）の create op の src を公開 URL へ更新する。
      // アップロード完了前に Undo / 削除された場合でも、後続の Redo / Undo で未解決の
      // プレースホルダ（blob:/data:）が復活しないようにするため。
      updateAttachmentCreateHistorySrc(objectId, publicSrc);
      const current = useCanvasDocStore.getState().objects[objectId];
      // すでに削除済み（Undo / 削除済み）なら本人用上書きだけ破棄して終わる。
      if (!current) {
        removeInFlightOp(overrideOpId);
        return;
      }
      // 公開 URL を全参加者へ反映（履歴は create 側の undo=delete に集約するため積まない）。
      const op: CanvasOp = {
        type: "object.attachment.update",
        ...makeMeta(),
        objectId,
        src: publicSrc,
      };
      sendOp(op);
      // 本人用のローカル blob 上書きを破棄し、以後は全員が公開 URL を表示する。
      removeInFlightOp(overrideOpId);
    },
    [makeMeta, sendOp, removeInFlightOp]
  );

  const discardAttachmentPlaceholder = useCallback<
    CanvasOpDispatcher["discardAttachmentPlaceholder"]
  >(
    (objectId, overrideOpId) => {
      removeInFlightOp(overrideOpId);
      // Redo で解決不能なプレースホルダを復活させないよう、履歴エントリも除去する。
      removeAttachmentCreateHistoryEntry(objectId);
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current) return;
      // 仮表示オブジェクトを全参加者から削除し、壊れた状態を残さない。
      sendOp(makeDeleteOp(objectId, ctx.actorUserId, ctx.actorDisplayName));
    },
    [removeInFlightOp, ctx.actorUserId, sendOp]
  );

  const createObjects = useCallback<CanvasOpDispatcher["createObjects"]>(
    (inputs) => {
      if (inputs.length === 0) return [];
      if (!canCreateObjects(inputs.length)) return [];
      const now = new Date().toISOString();
      const objects = useCanvasDocStore.getState().objects;
      let nextZ = computeNextZOrder(objects);
      const created: CanvasObject[] = [];
      for (const input of inputs) {
        const baseObject = clampCanvasObjectToCanvas({
          ...input,
          id: input.id ?? generateId(),
          version: 1,
          zOrder: nextZ,
          createdAt: now,
          createdBy: ctx.actorUserId,
          updatedAt: now,
          updatedBy: ctx.actorUserId,
        } as CanvasObject);
        const object: CanvasObject =
          baseObject.type === "sticky" && ctx.actorDisplayName
            ? { ...baseObject, updatedByName: ctx.actorDisplayName }
            : baseObject;
        if (!isCanvasObjectInsideCanvas(object)) {
          notifyOutOfBounds();
          return [];
        }
        created.push(object);
        nextZ += 1;
      }
      const ops: CanvasOp[] = created.map((object) => ({
        type: "object.create",
        clientOpId: generateId(),
        clientCreatedAt: now,
        actorUserId: ctx.actorUserId,
        ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
        object,
      }));
      dispatchMany(ops, {
        undo: created.map((object) => makeDeleteOp(object.id, ctx.actorUserId, ctx.actorDisplayName)),
        redo: ops,
      });
      return created;
    },
    [canCreateObjects, ctx.actorUserId, dispatchMany, notifyOutOfBounds]
  );

  const transformObject = useCallback<CanvasOpDispatcher["transformObject"]>(
    (objectId, transform) => {
      const objects = useCanvasDocStore.getState().objects;
      const current = objects[objectId];
      if (!current) return;
      // ロック中はロック切替（locked のみ）以外の transform を最終防衛として一切送らない。
      // ドラッグ・リサイズ・回転は pointerdown 時の locked チェックを行うが、開始後 pointerup
      // までの間に他クライアントから locked: true が反映されたケースを救う必要がある。
      if (current.locked && !isLockOnlyTransform(transform)) return;
      const inverse = pickCurrentTransform(current, transform);
      const op: CanvasOp = {
        type: "object.transform",
        ...makeMeta(),
        objectId,
        transform,
      };
      // 位置・サイズが変わる場合は、この図形を端点とするコネクタの保存 bbox も同じ履歴に含めて
      // 更新する（描画は端点から都度計算するが、範囲選択・ミニマップ・他参加者選択枠など AABB を
      // 参照する箇所が古い bbox を使わないようにするため）。
      const movedFrame = frameAfterTransform(current, transform);
      const connectorUpdates = movedFrame
        ? buildConnectorBboxUpdateOps(objects, new Map([[objectId, movedFrame]]), makeMeta)
        : { ops: [], undo: [] };
      if (connectorUpdates.ops.length > 0) {
        dispatchMany([op, ...connectorUpdates.ops], {
          undo: [
            { type: "object.transform", ...makeMeta(), objectId, transform: inverse },
            ...connectorUpdates.undo,
          ],
          redo: [op, ...connectorUpdates.ops],
        });
        return;
      }
      dispatch(op, {
        undo: [{ type: "object.transform", ...makeMeta(), objectId, transform: inverse }],
        redo: [op],
      });
    },
    [dispatch, dispatchMany, makeMeta]
  );

  // 複数オブジェクトの transform を 1 回の履歴エントリにまとめる。
  // グループドラッグなど「1 ユーザー操作 = 複数 op」を 1 回の Ctrl+Z で戻せるようにする。
  const transformObjects = useCallback<CanvasOpDispatcher["transformObjects"]>(
    (updates) => {
      if (updates.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      const ops: CanvasOp[] = [];
      const undo: CanvasOp[] = [];

      // 位置・サイズが変わった図形の確定後フレーム（コネクタ bbox 再計算用）。
      const movedFrames = new Map<string, ConnectorFrame>();
      const updatedIds = new Set<string>();

      for (const { objectId, transform } of updates) {
        const current = objects[objectId];
        if (!current) continue;
        // 個別 transformObject と同じ locked ガード。グループ内に locked が混ざっていても
        // 個別にスキップして残りは通す（=1 entry にロック解除前の状態を保持できる）。
        if (current.locked && !isLockOnlyTransform(transform)) continue;
        const inverse = pickCurrentTransform(current, transform);
        const op: CanvasOp = { type: "object.transform", ...makeMeta(), objectId, transform };
        ops.push(op);
        undo.push({ type: "object.transform", ...makeMeta(), objectId, transform: inverse });
        updatedIds.add(objectId);
        const movedFrame = frameAfterTransform(current, transform);
        if (movedFrame) movedFrames.set(objectId, movedFrame);
      }

      if (ops.length === 0) return;

      // 端点が動いたコネクタの保存 bbox も同じ履歴に含めて更新する（updates 自体に含まれる
      // コネクタは二重更新を避けるため除外する）。
      const connectorUpdates =
        movedFrames.size > 0
          ? buildConnectorBboxUpdateOps(objects, movedFrames, makeMeta, updatedIds)
          : { ops: [], undo: [] };

      dispatchMany([...ops, ...connectorUpdates.ops], {
        undo: [...undo, ...connectorUpdates.undo],
        redo: [...ops, ...connectorUpdates.ops],
      });
    },
    [dispatchMany, makeMeta]
  );

  const updateText = useCallback<CanvasOpDispatcher["updateText"]>(
    (objectId, text) => {
      const current = useCanvasDocStore.getState().objects[objectId];
      // ロック中は本文更新も行わない。useObjectTextEditor 側でも編集モードを抜けて
      // 保留中の debounce timer は破棄しているが、UI イベントや blur のタイミング差で
      // この経路に到達しても契約を守れるよう dispatcher 側でも防御する。
      if (!current || !("text" in current) || current.locked || current.text === text) return;
      const op: CanvasOp = {
        type: "object.text.update",
        ...makeMeta(),
        objectId,
        text,
      };
      dispatch(op, {
        undo: [{ type: "object.text.update", ...makeMeta(), objectId, text: current.text }],
        redo: [op],
      });
    },
    [dispatch, makeMeta]
  );

  const updateObjectStyle = useCallback<CanvasOpDispatcher["updateObjectStyle"]>(
    (objectId, style) => {
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current) return;
      if (current.locked) return;
      // 適用前の各フィールド値を取得し undo の逆 op を組み立てる。対応する field を持たない
      // オブジェクト種別では効果が無いため、style の中で実際に効果のあるフィールドだけ拾う。
      //
      // 注意（undo の制約）: object.style.update の op スキーマは optional な値の「未設定（undefined）」を
      // 明示的に表現できない（key を省略する = 変更しない）。そのため、変更前のフィールド値が
      // undefined だったケースでは、逆 op の対応キーを省略するしかなく、Undo してもフィールド値は
      // 「変更後の値のまま」となる。これは現状の op モデルに起因する既知の制約で、目に見える
      // 退行を起こすケースは「既定値で作成済みのオブジェクトに対する色 / サイズ変更」だけに限られる
      // （新規作成経路では sticky.color, text.fontSize, pen-stroke.color は常に値を持つ）。
      // 将来的に「unset 用センチネル」を導入する場合はここを拡張する。
      const effectiveStyle: Partial<{
        color: string;
        fontSize: number;
        fill: string;
        stroke: string;
        textAlign: "left" | "center" | "right";
      }> = {};
      const inverseStyle: Partial<{
        color: string;
        fontSize: number;
        fill: string;
        stroke: string;
        textAlign: "left" | "center" | "right";
      }> = {};
      // color フィールドを持つオブジェクト種別だけを白リスト化して扱う（image / video / link-card 等は除外）。
      const colorBearingTypes: ReadonlyArray<CanvasObject["type"]> = [
        "sticky",
        "text",
        "pen-stroke",
      ];
      if (style.color !== undefined && colorBearingTypes.includes(current.type)) {
        effectiveStyle.color = style.color;
        const prev = (current as { color?: string | undefined }).color;
        if (prev !== undefined) inverseStyle.color = prev;
      }
      if (style.fontSize !== undefined && current.type === "text") {
        effectiveStyle.fontSize = style.fontSize;
        if (current.fontSize !== undefined) inverseStyle.fontSize = current.fontSize;
      }
      if (style.fill !== undefined && current.type === "shape") {
        effectiveStyle.fill = style.fill;
        if (current.fill !== undefined) inverseStyle.fill = current.fill;
      }
      if (style.stroke !== undefined && current.type === "shape") {
        effectiveStyle.stroke = style.stroke;
        if (current.stroke !== undefined) inverseStyle.stroke = current.stroke;
      }
      // 文字寄せは text / sticky にのみ適用する。未指定（=左寄せ）から変更する場合は逆 op を
      // 省略するしかなく、Undo で左寄せへ戻らない既知の制約があるため、逆 op が確実に作れる
      // よう「変更前が未指定なら left を逆 op に積む」ことで Undo でも左寄せへ戻せるようにする。
      if (
        style.textAlign !== undefined &&
        (current.type === "text" || current.type === "sticky")
      ) {
        effectiveStyle.textAlign = style.textAlign;
        inverseStyle.textAlign = current.textAlign ?? "left";
      }
      if (Object.keys(effectiveStyle).length === 0) return;
      const op: CanvasOp = {
        type: "object.style.update",
        ...makeMeta(),
        objectId,
        style: effectiveStyle,
      };
      const undoOp: CanvasOp | null =
        Object.keys(inverseStyle).length > 0
          ? {
              type: "object.style.update",
              ...makeMeta(),
              objectId,
              style: inverseStyle,
            }
          : null;
      dispatch(op, {
        undo: undoOp ? [undoOp] : [],
        redo: [op],
      });
    },
    [dispatch, makeMeta]
  );

  const updateAttachmentSrc = useCallback<CanvasOpDispatcher["updateAttachmentSrc"]>(
    (objectId, src) => {
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current) return;
      const previousSrc = getAttachmentSrc(current);
      const op: CanvasOp = {
        type: "object.attachment.update",
        ...makeMeta(),
        objectId,
        src,
      };
      dispatch(op, {
        undo: previousSrc
          ? [{ type: "object.attachment.update", ...makeMeta(), objectId, src: previousSrc }]
          : [],
        redo: [op],
      });
    },
    [dispatch, makeMeta]
  );

  const updateCanvasMeta = useCallback<CanvasOpDispatcher["updateCanvasMeta"]>(
    (input) => {
      const current = useCanvasDocStore.getState().meta;
      const nextTitle = input.title?.trim();
      const op: CanvasOp = {
        type: "canvas.meta.update",
        ...makeMeta(),
        ...(nextTitle ? { title: nextTitle } : {}),
        ...(input.thumbnailKey !== undefined ? { thumbnailKey: input.thumbnailKey } : {}),
      };
      if (op.title === undefined && op.thumbnailKey === undefined) return;
      dispatch(op, {
        undo: [
          {
            type: "canvas.meta.update",
            ...makeMeta(),
            ...(op.title !== undefined ? { title: current.title } : {}),
            ...(op.thumbnailKey !== undefined && current.thumbnailKey !== undefined
              ? { thumbnailKey: current.thumbnailKey }
              : {}),
          },
        ],
        redo: [op],
      });
    },
    [dispatch, makeMeta]
  );

  const deleteObjects = useCallback<CanvasOpDispatcher["deleteObjects"]>(
    (objectIds) => {
      if (objectIds.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      // 削除対象の図形を接続先とするコネクタも巻き込んで削除する（宙ぶらりんな矢印を残さない）。
      const requested = new Set(objectIds);
      const connectorIds = collectConnectorCascadeDeleteIds(Object.values(objects), requested);
      const allIds = [...objectIds, ...connectorIds];
      const targets = allIds.map((id) => objects[id]).filter((o): o is CanvasObject => Boolean(o));
      if (targets.length === 0) return;
      const ops = targets.map((object) =>
        makeDeleteOp(object.id, ctx.actorUserId, ctx.actorDisplayName)
      );
      // Undo の復元（object.create）では、サーバの connector 端点検証に通るよう端点図形を先に、
      // コネクタを後に作成する。選択順が「コネクタ → 端点」のときに Undo でコネクタ create が
      // 端点より先に送られて reject され、矢印だけ復元されない事態を防ぐ（作成順の不変条件を
      // 削除 Undo の復元経路にも適用する）。delete（ops/redo）の順序は結果に影響しない。
      const undoTargets = [
        ...targets.filter((object) => object.type !== "connector"),
        ...targets.filter((object) => object.type === "connector"),
      ];
      dispatchMany(ops, {
        undo: undoTargets.map((object) => makeCreateOp(object, ctx.actorUserId, ctx.actorDisplayName)),
        redo: ops,
      });
    },
    [ctx.actorUserId, ctx.actorDisplayName, dispatchMany]
  );

  const deleteObject = useCallback<CanvasOpDispatcher["deleteObject"]>(
    (objectId) => {
      const objects = useCanvasDocStore.getState().objects;
      const current = objects[objectId];
      if (!current) return;
      // 接続先として参照しているコネクタも一緒に削除し、宙ぶらりんな矢印を残さない。
      // 1 回の履歴エントリにまとめ、Undo で図形とコネクタがそろって復元されるようにする。
      const connectorIds = collectConnectorCascadeDeleteIds(Object.values(objects), new Set([objectId]));
      if (connectorIds.length > 0) {
        deleteObjects([objectId, ...connectorIds]);
        return;
      }
      const op = makeDeleteOp(objectId, ctx.actorUserId, ctx.actorDisplayName);
      dispatch(op, {
        undo: [makeCreateOp(current, ctx.actorUserId, ctx.actorDisplayName)],
        redo: [op],
      });
    },
    [ctx.actorUserId, ctx.actorDisplayName, dispatch, deleteObjects]
  );

  const commitPenStroke = useCallback<CanvasOpDispatcher["commitPenStroke"]>(
    (points, color, thickness, _boundingBox, objectId) => {
      if (!canCreateObjects(1)) return null;
      const id = objectId ?? generateId();
      const now = new Date().toISOString();
      const objects = useCanvasDocStore.getState().objects;
      const nextZ = computeNextZOrder(objects);
      const clippedPoints = points.map((point) => clampPointToCanvas(point));
      const boundingBox = computePenStrokeFrame(clippedPoints, thickness);
      const stroke: PenStrokeCanvasObject = {
        type: "pen-stroke",
        id,
        x: boundingBox.x,
        y: boundingBox.y,
        w: boundingBox.w,
        h: boundingBox.h,
        zOrder: nextZ,
        version: 1,
        color,
        thickness,
        points: clippedPoints,
        createdAt: now,
        createdBy: ctx.actorUserId,
        updatedAt: now,
        updatedBy: ctx.actorUserId,
      };
      const op: CanvasOp = {
        type: "pen.stroke.commit",
        clientOpId: generateId(),
        clientCreatedAt: now,
        actorUserId: ctx.actorUserId,
        ...(ctx.actorDisplayName ? { actorDisplayName: ctx.actorDisplayName } : {}),
        object: stroke,
      };
      dispatch(op, {
        undo: [makeDeleteOp(stroke.id, ctx.actorUserId, ctx.actorDisplayName)],
        redo: [op],
      });
      return stroke;
    },
    [canCreateObjects, ctx.actorUserId, ctx.actorDisplayName, dispatch]
  );

  const undo = useCallback((): void => {
    const entry = undoStack.pop();
    if (!entry) return;
    // ロック衝突がある場合は履歴に戻して何もしない（再ロック解除後は再度 Ctrl+Z 可能）。
    // dispatcher 側 (transformObject / updateObjectStyle) の locked ガードを履歴経路でも
    // 迂回されないようにする。transform だけでなく style.update も対象にする。
    if (hasLockedObjectMutationConflict(entry.undo)) {
      undoStack.push(entry);
      return;
    }
    for (const op of entry.undo) {
      sendOp(refreshOpMeta(op, ctx.actorUserId, ctx.actorDisplayName));
    }
    redoStack.push(entry);
  }, [ctx.actorUserId, ctx.actorDisplayName, sendOp]);

  const redo = useCallback((): void => {
    const entry = redoStack.pop();
    if (!entry) return;
    if (hasLockedObjectMutationConflict(entry.redo)) {
      redoStack.push(entry);
      return;
    }
    for (const op of entry.redo) {
      sendOp(refreshOpMeta(op, ctx.actorUserId, ctx.actorDisplayName));
    }
    undoStack.push(entry);
  }, [ctx.actorUserId, ctx.actorDisplayName, sendOp]);

  const previewTransforms = useCallback<CanvasOpDispatcher["previewTransforms"]>(
    (updates) => {
      for (const { objectId, transform } of updates) {
        const sanitized = pickTransientTransform(transform);
        if (Object.keys(sanitized).length === 0) continue;
        realtime.sendMessage({
          type: "op.transient",
          op: {
            type: "object.transient.transform",
            clientOpId: generateId(),
            objectId,
            transform: sanitized,
          },
        });
      }
    },
    [realtime]
  );

  const previewPenAppend = useCallback<CanvasOpDispatcher["previewPenAppend"]>(
    (strokeId, points) => {
      if (points.length === 0) return;
      realtime.sendMessage({
        type: "op.transient",
        op: {
          type: "pen.stroke.append",
          clientOpId: generateId(),
          strokeId,
          points: points.map((p) => ({ x: p.x, y: p.y, pressure: p.pressure })),
        },
      });
    },
    [realtime]
  );

  return {
    createObject,
    createObjects,
    createConnector,
    createAttachmentPlaceholder,
    finalizeAttachmentPlaceholder,
    discardAttachmentPlaceholder,
    transformObject,
    transformObjects,
    updateText,
    updateObjectStyle,
    updateAttachmentSrc,
    updateCanvasMeta,
    deleteObject,
    deleteObjects,
    undo,
    redo,
    commitPenStroke,
    previewTransforms,
    previewPenAppend,
  };
}

// 一時 transform op が許容するフィールド（x/y/w/h/rotation）だけを取り出す。
// zOrder / locked などの永続専用フィールドはゴーストプレビューでは扱わない。
function pickTransientTransform(
  transform: ObjectTransformPatch
): Partial<{ x: number; y: number; w: number; h: number; rotation: number }> {
  const picked: Partial<{ x: number; y: number; w: number; h: number; rotation: number }> = {};
  if (transform.x !== undefined) picked.x = transform.x;
  if (transform.y !== undefined) picked.y = transform.y;
  if (transform.w !== undefined) picked.w = transform.w;
  if (transform.h !== undefined) picked.h = transform.h;
  if (transform.rotation !== undefined) picked.rotation = transform.rotation;
  return picked;
}

// 仮表示 create の履歴エントリ（undo / redo 両側の object.create）の src を、アップロード完了後の
// 確定 src（公開 URL）に更新する。これをしないと、
//  - Undo -> Redo で redo 側がプレースホルダ付き create を復元する
//  - アップロード完了前の通常削除で undo 側に保存された blob:/data: が復元される
// といった経路で、アップロードは済んでいるのに永続的に「読み込み中」表示になる。
// このため undo / redo の両側を書き換える。
function updateAttachmentCreateHistorySrc(objectId: string, src: string): void {
  for (const stack of [undoStack, redoStack]) {
    for (let i = 0; i < stack.length; i++) {
      const entry = stack[i];
      if (!entry) continue;
      let changed = false;
      const rewriteCreateSrc = (op: CanvasOp): CanvasOp => {
        if (
          op.type === "object.create" &&
          op.object.id === objectId &&
          (op.object.type === "image" || op.object.type === "video")
        ) {
          changed = true;
          return { ...op, object: { ...op.object, src } } as CanvasOp;
        }
        return op;
      };
      const undo = entry.undo.map(rewriteCreateSrc);
      const redo = entry.redo.map(rewriteCreateSrc);
      if (changed) stack[i] = { undo, redo };
    }
  }
}

// 仮表示 create の履歴エントリを除去する。アップロード失敗時に呼び、二度と解決できない
// 壊れたプレースホルダを Undo / Redo で復活させないようにする。
// create op は履歴エントリの redo 側（新規作成）だけでなく、アップロード完了前に通常削除した
// 場合の undo 側（makeCreateOp による復元用）にも積まれるため、undo / redo の両側を対象にする。
function removeAttachmentCreateHistoryEntry(objectId: string): void {
  const hasCreate = (entry: HistoryEntry): boolean =>
    [...entry.undo, ...entry.redo].some(
      (op) => op.type === "object.create" && op.object.id === objectId
    );
  for (const stack of [undoStack, redoStack]) {
    for (let i = stack.length - 1; i >= 0; i--) {
      const entry = stack[i];
      if (entry && hasCreate(entry)) stack.splice(i, 1);
    }
  }
}

function makeCreateOp(
  object: CanvasObject,
  actorUserId: string,
  actorDisplayName?: string | undefined
): CanvasOp {
  return {
    type: "object.create",
    clientOpId: generateId(),
    clientCreatedAt: new Date().toISOString(),
    actorUserId,
    ...(actorDisplayName ? { actorDisplayName } : {}),
    object,
  };
}

function makeDeleteOp(
  objectId: string,
  actorUserId: string,
  actorDisplayName?: string | undefined
): CanvasOp {
  return {
    type: "object.delete",
    clientOpId: generateId(),
    clientCreatedAt: new Date().toISOString(),
    actorUserId,
    ...(actorDisplayName ? { actorDisplayName } : {}),
    objectId,
  };
}

function refreshOpMeta(
  op: CanvasOp,
  actorUserId: string,
  actorDisplayName: string | undefined
): CanvasOp {
  return {
    ...op,
    clientOpId: generateId(),
    clientCreatedAt: new Date().toISOString(),
    actorUserId,
    // undo/redo の実行時点での displayName で上書きする（再生時の名前が記録時点と乖離しないように）。
    ...(actorDisplayName ? { actorDisplayName } : {}),
  } as CanvasOp;
}

// transform が「locked のみで構成される＝ロック切替」かを判定する。
// ロック中でもロック切替（特に解除）の経路は通したいので、ガード判定の鍵となる。
function isLockOnlyTransform(transform: ObjectTransformPatch): boolean {
  const transformKeys = Object.keys(transform);
  return transformKeys.length > 0 && transformKeys.every((key) => key === "locked");
}

// undo / redo に積まれた op 集合のうち、ロック中オブジェクトに対する
// 「編集系」op が含まれるかを判定する。
// 対象は次の 2 種類:
//  - object.transform: 位置・サイズ・回転・zOrder などの変形（ロック切替単独だけは許可）
//  - object.style.update: 色・フォントサイズ・塗り・線の見た目変更（常にロックで抑止）
// 含まれている場合、そのエントリは履歴に戻して送信スキップする。
function hasLockedObjectMutationConflict(ops: readonly CanvasOp[]): boolean {
  return ops.some((op) => {
    if (op.type !== "object.transform" && op.type !== "object.style.update") return false;
    const current = useCanvasDocStore.getState().objects[op.objectId];
    if (current?.locked !== true) return false;
    if (op.type === "object.transform") return !isLockOnlyTransform(op.transform);
    // object.style.update はロック切替のような例外経路を持たないため、ロック中なら常に衝突扱い。
    return true;
  });
}

function pickCurrentTransform(
  object: CanvasObject,
  requested: ObjectTransformPatch
): ObjectTransformPatch {
  const inverse: ObjectTransformPatch = {};
  if (requested.x !== undefined) inverse.x = object.x;
  if (requested.y !== undefined) inverse.y = object.y;
  if (requested.w !== undefined) inverse.w = object.w;
  if (requested.h !== undefined) inverse.h = object.h;
  if (requested.rotation !== undefined) inverse.rotation = object.rotation ?? 0;
  if (requested.zOrder !== undefined) inverse.zOrder = object.zOrder;
  if (requested.locked !== undefined) inverse.locked = object.locked ?? false;
  return inverse;
}

function getAttachmentSrc(object: CanvasObject): string | undefined {
  if (object.type === "image" || object.type === "video") return object.src;
  if (object.type === "link-card") return object.imageUrl;
  return undefined;
}

/**
 * transform 適用後の図形フレーム（x/y/w/h）を返す。位置・サイズのいずれも変化しない
 * （zOrder / locked / rotation のみの）transform や、コネクタ自身の場合は null を返す。
 */
function frameAfterTransform(
  current: CanvasObject,
  transform: ObjectTransformPatch
): ConnectorFrame | null {
  if (current.type === "connector") return null;
  const geometryChanged =
    transform.x !== undefined ||
    transform.y !== undefined ||
    transform.w !== undefined ||
    transform.h !== undefined;
  if (!geometryChanged) return null;
  return {
    x: transform.x ?? current.x,
    y: transform.y ?? current.y,
    w: transform.w ?? current.w,
    h: transform.h ?? current.h,
  };
}

/**
 * 端点が動いたコネクタの保存 bbox（x/y/w/h）を最新ルートに合わせて更新する transform op 群を返す。
 * movedFrames は「移動後フレーム」を持つ図形 id → frame。未移動の端点は objects の現在値を使う。
 * excludeIds に含まれるコネクタ id は対象外（呼び出し側で別途 transform 済みの場合に二重更新を防ぐ）。
 * bbox が変化しないコネクタは op を生成しない。
 */
function buildConnectorBboxUpdateOps(
  objects: Record<string, CanvasObject>,
  movedFrames: ReadonlyMap<string, ConnectorFrame>,
  makeMeta: () => OpMeta,
  excludeIds?: ReadonlySet<string>
): { ops: CanvasOp[]; undo: CanvasOp[] } {
  const ops: CanvasOp[] = [];
  const undo: CanvasOp[] = [];
  for (const object of Object.values(objects)) {
    if (object.type !== "connector") continue;
    if (excludeIds?.has(object.id)) continue;
    if (!movedFrames.has(object.from.objectId) && !movedFrames.has(object.to.objectId)) continue;
    const fromFrame = movedFrames.get(object.from.objectId) ?? frameFromObject(objects[object.from.objectId]);
    const toFrame = movedFrames.get(object.to.objectId) ?? frameFromObject(objects[object.to.objectId]);
    if (!fromFrame || !toFrame) continue;
    const bbox = connectorStoredFrame(fromFrame, object.from.side, toFrame, object.to.side);
    if (bbox.x === object.x && bbox.y === object.y && bbox.w === object.w && bbox.h === object.h) {
      continue;
    }
    ops.push({
      type: "object.transform",
      ...makeMeta(),
      objectId: object.id,
      transform: { x: bbox.x, y: bbox.y, w: bbox.w, h: bbox.h },
    });
    undo.push({
      type: "object.transform",
      ...makeMeta(),
      objectId: object.id,
      transform: { x: object.x, y: object.y, w: object.w, h: object.h },
    });
  }
  return { ops, undo };
}

/** オブジェクトから接続用フレーム（x/y/w/h）を取り出す。存在しなければ null。 */
function frameFromObject(object: CanvasObject | undefined): ConnectorFrame | null {
  if (!object) return null;
  return { x: object.x, y: object.y, w: object.w, h: object.h };
}

/** 既存オブジェクトの最大 zOrder + 1 を返す（簡易な「最前面」割り当て）。 */
function computeNextZOrder(objects: Record<string, CanvasObject>): number {
  let max = 0;
  for (const obj of Object.values(objects)) {
    if (obj.zOrder > max) max = obj.zOrder;
  }
  return max + 1;
}

// 補助型: createObject に渡す引数を 1 種類ずつ作るためのヘルパー。
export type StickyDraftInput = Omit<
  StickyCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type TextDraftInput = Omit<
  TextCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type ShapeDraftInput = Omit<
  ShapeCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type ImageDraftInput = Omit<
  ImageCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type VideoDraftInput = Omit<
  VideoCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type LinkCardDraftInput = Omit<
  LinkCardCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
